import type { FC, ReactNode } from "react";

import {
  Box,
  Column,
  Heading,
  Row,
  SegmentedControl,
  SegmentedControlOption,
  type BoxProps,
} from "@hightouch/htui";

import { PageSuspense } from "src/components/loading";
import {
  type RouteTab,
  RouteTabs,
  useTabState,
} from "src/components/route-tabs";
import { useLocation, useNavigate } from "src/router";
import { SIZES } from "src/ui/box";

import { withCurrentPageCrumb } from "./crumbs";
import {
  CrumbBarEndSlot,
  useCrumbBar,
  useHasCrumbBars,
} from "src/components/layout/nav/mini-nav/mini-nav-context";
import {
  CrumbBarTrail,
  NewPage,
  type NewPageProps,
  PageCardContent,
  PageCardHeader,
} from "./new-page";
import { PageCardTopBar, PageTitleRows } from "./page-card-top-bar";
import { DefaultPageContainerPadding } from "./page-container";
import type { Crumb, PageBg } from "./types";

// Props shared between detail page and detail page tab
type SharedDetailPageProps = {
  hasBottomPadding?: boolean;
  bg?: PageBg;
};

/**
 * The page variant determines the background color and max width of the content.
 * @default "dashboard"
 */
type DetailPageVariant =
  | "dashboard" // Dark background, full width. Blank canvas.
  | "dashboardLight" // Light background, full width. Blank canvas.
  | "card" // Dark background, constrained page width. Used for overviews, summaries, and card-heavy pages.
  | "cardContained" // Like "card", but height-contained: content never grows past the window, and the content region scrolls instead of the document. Lets a card fill the remaining height (flex={1}) and scroll its own panes.
  | "form" // Dark background, constrained small width.
  | "table" // White background, full width.
  | "page" // White background, constrained page width.
  | "pageDark" // Dark background, constrained page width.
  | "fullPage" // White background, full width, full height. Often used for code or config pages.
  | "fullPageDark" // Dark background, full width, full height. Used for SegmentedControl-based detail pages.
  | "dashboardContainedLightHeader"; // Gray content, full width, height-contained, with a white segmented header. Decouples the header bg from the content bg so the page header stays white while content stays gray and columns scroll independently.

export type DetailPageTab = RouteTab &
  SharedDetailPageProps & {
    variant: DetailPageVariant;
    sidebar?: NewPageProps["sidebar"];
    status?: "empty" | "success" | "error";
  };

type NavigationMode = "tabs" | "segmented";

/**
 * How the title row copes when its content and actions cannot share one line.
 * "row" keeps them on one line and lets the header absorb the deficit. "wrap"
 * lets the actions fall to their own line first, for a header whose content
 * (labels, badges) must stay legible at narrow widths.
 */
type HeaderLayout = "row" | "wrap";

/**
 * Below this the title row drops its actions to the next line. Capped at the
 * width actually on offer, so a row narrower than the floor still hands the
 * header every pixel it has instead of overflowing into the card's clip.
 */
const HEADER_WRAP_MIN_WIDTH = "min(var(--chakra-sizes-2xs), 100%)";

type BasePageProps = Omit<NewPageProps, "crumbBarEnd"> &
  SharedDetailPageProps & {
    /** Breadcrumbs in the page card's top bar. The current page
     *  (`heading ?? title`) is appended when the trail ends in a link. */
    crumbs?: Crumb[];
    /** Title/actions row content. Independent of `crumbs` — both render. */
    header?: ReactNode;
    /** Full-width content rendered *inside* the page card, below the breadcrumb
     *  and above the header. For page-level notices that should sit above the
     *  card (approval banners, page alerts) use `outsideTopbar` instead. */
    banner?: ReactNode;
    /**
     * Path depth at which the active tab is matched.
     * @default 4
     */
    tabDepth?: number;
    /** On-screen page title rendered as a Heading. */
    heading?: string;
    /** Right-aligned controls in the title row. Rendered in the top bar in
     *  segmented mode, or when crumbs exist with no other title-row content. */
    actions?: ReactNode;
    /** Right-aligned controls on the tab strip (ignored in segmented mode). */
    tabsActions?: ReactNode;
    /**
     * In segmented mode, route crumbs into NewPage's own top-bar strip (a
     * standalone breadcrumb trail above the card) instead of rendering them
     * inline inside the segmented top bar. Default (`false`) keeps the
     * single-bar layout — crumbs share one bar with the SegmentedControl and
     * actions. Opt in only for pages that want the crumb trail as a separate
     * wayfinding strip (Ad Studio feed/treatment pages, whose parents-only
     * trail reads as a stray back-link when rendered inline). No effect
     * outside segmented mode.
     */
    segmentedCrumbsInTopBarStrip?: boolean;
  };

// Props when tabs are provided (variant is the fallback for URLs no tab matches)
type PropsWithTabs = BasePageProps & {
  tabs: Array<DetailPageTab>;
  isTabsLoading?: boolean;
  /** Collapse tabs that don't fit into a trailing "N more" menu instead of
   *  squishing the strip. The active tab is always kept visible. */
  tabsCollapsible?: boolean;
  variant?: DetailPageVariant;
  /**
   * How tab navigation renders: a tab bar below the header, or a
   * SegmentedControl in the top bar.
   * @default "tabs"
   */
  navigationMode?: NavigationMode;
  /**
   * Title-row behavior when the header and actions cannot share a line.
   *
   * @default "row"
   */
  headerLayout?: HeaderLayout;
};

// Props when tabs are not provided (variant required)
type PropsWithoutTabs = BasePageProps & {
  tabs?: undefined;
  isTabsLoading?: undefined;
  tabsCollapsible?: undefined;
  variant: DetailPageVariant;
  navigationMode?: never;
  headerLayout?: never;
};

export type Props = PropsWithTabs | PropsWithoutTabs;

const variantStyles: Record<
  DetailPageVariant,
  {
    bg: PageBg;
    maxWidth: keyof typeof SIZES;
    contentFullHeight?: boolean;
    /** Header background override; when unset the header follows `bg`. */
    headerBg?: PageBg;
    content: BoxProps;
  }
> = {
  dashboard: {
    bg: "dark",
    maxWidth: "full",
    content: {},
  },
  dashboardLight: {
    bg: "light",
    maxWidth: "full",
    content: {},
  },
  card: {
    bg: "dark",
    maxWidth: "page",
    content: {
      height: "100%",
      minH: 0,
      mt: 0,
    },
  },
  cardContained: {
    bg: "dark",
    maxWidth: "page",
    contentFullHeight: true,
    content: {
      minH: 0,
      mt: 0,
      // The content region owns the scroll, so flex children can fill the
      // window instead of being clipped by the page card.
      overflowY: "auto",
    },
  },
  form: {
    bg: "dark",
    maxWidth: "small",
    content: {
      flex: 1,
    },
  },
  table: {
    bg: "light",
    maxWidth: "full",
    content: {},
  },
  page: {
    bg: "light",
    maxWidth: "page",
    content: {
      height: "100%",
      minH: 0,
      mt: 0,
    },
  },
  pageDark: {
    bg: "dark",
    maxWidth: "page",
    content: {
      height: "100%",
      minH: 0,
      mt: 0,
    },
  },
  fullPage: {
    bg: "light",
    maxWidth: "full",
    contentFullHeight: true,
    content: {},
  },
  fullPageDark: {
    bg: "dark",
    maxWidth: "full",
    contentFullHeight: true,
    content: {},
  },
  dashboardContainedLightHeader: {
    bg: "dark",
    maxWidth: "full",
    contentFullHeight: true,
    headerBg: "light",
    content: {},
  },
};

export const DetailPage: FC<Readonly<Props>> = ({
  hasBottomPadding,
  hideBanners,
  children,
  header,
  banner,
  heading,
  actions,
  tabsActions,
  crumbs,
  segmentedCrumbsInTopBarStrip = false,
  outsideTopbar,
  sidebar,
  sidebarPanel,
  tabs,
  isTabsLoading,
  tabsCollapsible,
  title,
  tabDepth = 4,
  variant = "dashboard",
  navigationMode = "tabs",
  headerLayout = "row",
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeTab } = useTabState(tabs ?? [], tabDepth);

  const isPaddingBottom = activeTab?.hasBottomPadding ?? hasBottomPadding;

  const activeVariantStyles = activeTab?.variant
    ? variantStyles[activeTab.variant]
    : variantStyles[variant];

  const isFullWidth = activeVariantStyles.maxWidth === "full";
  const backgroundColor = activeTab?.bg ?? activeVariantStyles.bg;
  const headerBackgroundColor = activeVariantStyles.headerBg ?? backgroundColor;
  const maxWidth = activeVariantStyles.maxWidth;
  const isFullHeight = activeVariantStyles.contentFullHeight;
  const pageCardContentStyles = activeVariantStyles.content;

  const content = isFullWidth ? (
    children
  ) : (
    <Column flex={1} minH={0} w="100%" maxWidth={SIZES[maxWidth]} mx="auto">
      {children}
    </Column>
  );

  const isSegmentedMode = navigationMode === "segmented" && Boolean(tabs);
  const isWrappingHeader = headerLayout === "wrap" && !isSegmentedMode;

  const resolvedCrumbs = crumbs?.length
    ? withCurrentPageCrumb(crumbs, heading ?? title)
    : undefined;

  // With crumbs and no other title-row content, actions collapse into the top
  // bar (a slim utility strip, py 2) instead of an actions-only row below it.
  const actionsInTopBar =
    !tabs && !header && !heading && Boolean(resolvedCrumbs) && Boolean(actions);

  const newPageCrumbs =
    (isSegmentedMode && !segmentedCrumbsInTopBarStrip) || actionsInTopBar
      ? undefined
      : resolvedCrumbs;

  const hasHeader =
    !actionsInTopBar && Boolean(header || heading || actions || tabs);
  // This page's own top bar carries its crumbs, so it is the card's crumb bar; NewPage draws the bar for every other page.
  const ownsCrumbBar = Boolean(resolvedCrumbs) && !newPageCrumbs;
  const isProductBar = useCrumbBar(ownsCrumbBar);
  const productBarProps = {
    isSticky: isProductBar && !isFullHeight,
    "data-nav-toggle-row": isProductBar || undefined,
  };
  const hasCrumbBars = useHasCrumbBars();
  // A crumb bar drawn above this page's own top bar takes the card's rounded corners.
  const isBelowCrumbBar =
    Boolean(newPageCrumbs) || (hasCrumbBars && !isProductBar);

  const segmentedNav =
    isSegmentedMode && tabs ? (
      <Box display="flex" justifyContent="center" width="sm">
        <SegmentedControl
          value={activeTab?.path ?? ""}
          onChange={(value: string) =>
            navigate({ pathname: value, search: location.search })
          }
          width="fit-content"
          size="md"
        >
          {tabs.map((tab) => (
            <SegmentedControlOption
              key={tab.path === "" ? "index" : tab.path}
              label={tab.title}
              value={tab.path}
              isDisabled={tab.isDisabled}
              status={tab.status}
            />
          ))}
        </SegmentedControl>
      </Box>
    ) : null;

  const segmentedBar = (
    <PageCardTopBar
      {...productBarProps}
      {...(isBelowCrumbBar && { borderTopRadius: "none" })}
      py={4}
      pr={6}
      gap={4}
      bg={headerBackgroundColor === "dark" ? "base.lightBackground" : "white"}
    >
      <Row justify="space-between" align="center" flex={1} minWidth={0} gap={4}>
        <Row flex={1} minWidth={0} align="center" gap={4}>
          {!segmentedCrumbsInTopBarStrip && resolvedCrumbs && (
            <CrumbBarTrail
              crumbs={resolvedCrumbs}
              isProductBar={isProductBar}
            />
          )}
          {header}
          {heading && <Heading size="xl">{heading}</Heading>}
        </Row>

        {segmentedNav}

        <Row flex={1} justify="flex-end" align="center" gap={2} minWidth={0}>
          {actions && (
            <Row align="center" gap={2}>
              {actions}
            </Row>
          )}
          {isProductBar && <CrumbBarEndSlot />}
        </Row>
      </Row>
    </PageCardTopBar>
  );
  const actionsBar = resolvedCrumbs && (
    <PageCardTopBar {...productBarProps} py={2} pr={6}>
      <Row justify="space-between" align="center" flex={1} minWidth={0} gap={4}>
        <CrumbBarTrail crumbs={resolvedCrumbs} isProductBar={isProductBar} />
        <Row align="center" gap={2} flexShrink={0}>
          {actions}
          {isProductBar && <CrumbBarEndSlot />}
        </Row>
      </Row>
    </PageCardTopBar>
  );
  // Outside the card's suspense boundary, so the crumbs stay put while the page's content loads.
  const ownCrumbBar = ownsCrumbBar
    ? isSegmentedMode
      ? segmentedBar
      : actionsBar
    : undefined;

  return (
    <NewPage
      title={title ?? heading}
      hideBanners={hideBanners}
      contain={isFullHeight}
      outsideTopbar={outsideTopbar}
      sidebar={activeTab?.sidebar ?? sidebar}
      sidebarPanel={sidebarPanel}
      crumbs={newPageCrumbs}
      topBar={ownCrumbBar}
    >
      {banner}
      {isSegmentedMode ? (
        !ownsCrumbBar && segmentedBar
      ) : (
        <>
          {hasHeader && (
            <PageTitleRows>
              {/* A wrapping header grows instead of squeezing, so it must not
                  be shrunk by a height-contained card (PageCardTopBar pins
                  itself for the same reason). */}
              <PageCardHeader
                removeBoxShadow
                flexShrink={isWrappingHeader ? 0 : undefined}
              >
                {header && actions ? (
                  <Row
                    justify="space-between"
                    align="center"
                    flex={1}
                    minWidth={0}
                    gap={4}
                    flexWrap={isWrappingHeader ? "wrap" : undefined}
                  >
                    {/* Line breaking reads each item's hypothetical size, so the
                        `flex: 1 1 0%` basis below can never push the actions to
                        a line of their own. A real basis and floor can. */}
                    <Row
                      flex={isWrappingHeader ? "1 1 auto" : 1}
                      minWidth={isWrappingHeader ? HEADER_WRAP_MIN_WIDTH : 0}
                    >
                      {header}
                    </Row>
                    {/* max-width, not shrink: the group keeps its content size
                        while the cap lets it break onto further lines instead
                        of running past the card's clip. */}
                    <Row
                      align="center"
                      flexShrink={0}
                      gap={4}
                      flexWrap={isWrappingHeader ? "wrap" : undefined}
                      justify={isWrappingHeader ? "flex-end" : undefined}
                      maxWidth={isWrappingHeader ? "100%" : undefined}
                      ml={isWrappingHeader ? "auto" : undefined}
                    >
                      {actions}
                    </Row>
                  </Row>
                ) : (
                  <>
                    {header}
                    {heading || actions ? (
                      <Row
                        justify="space-between"
                        align="center"
                        flex={1}
                        minWidth={0}
                        gap={4}
                      >
                        {heading && <Heading size="xl">{heading}</Heading>}
                        {actions && (
                          <Row align="center" ml="auto" gap={4}>
                            {actions}
                          </Row>
                        )}
                      </Row>
                    ) : null}
                  </>
                )}
              </PageCardHeader>

              {tabs ? (
                tabsActions ? (
                  <Row
                    align="flex-end"
                    justify="space-between"
                    gap={4}
                    px={DefaultPageContainerPadding.X}
                  >
                    <RouteTabs
                      tabs={tabs}
                      isLoading={isTabsLoading}
                      depth={tabDepth}
                      collapsible={tabsCollapsible}
                      flex={1}
                      minWidth={0}
                    />
                    <Row align="center" gap={2} pb={2} flexShrink={0}>
                      {tabsActions}
                    </Row>
                  </Row>
                ) : (
                  <RouteTabs
                    tabs={tabs}
                    isLoading={isTabsLoading}
                    depth={tabDepth}
                    collapsible={tabsCollapsible}
                    px={DefaultPageContainerPadding.X}
                  />
                )
              ) : (
                <Box
                  borderBottomWidth="hairline"
                  borderBottomStyle="solid"
                  borderColor="base.divider"
                />
              )}
            </PageTitleRows>
          )}
        </>
      )}

      <PageCardContent
        flush={isFullWidth}
        variant={backgroundColor}
        pb={isPaddingBottom ? DefaultPageContainerPadding.Bottom : 0}
        {...pageCardContentStyles}
      >
        <PageSuspense>{content}</PageSuspense>
      </PageCardContent>
    </NewPage>
  );
};
